import { WarningFilled } from '@ant-design/icons'
import { Alarm, useAlarmList } from '@services/alarm'
import Loading from '@shared/innmaxUI/Loading'
import cx from 'classnames'
import moment from 'moment'
import React from 'react'
import MobilePageDrawer, {
  IMobilePageDrawerProps,
} from 'src/components/MobilePageDrawer'
import styled from 'styled-components'

const query = {
  startAt: moment().startOf('month').valueOf(),
  endAt: moment().endOf('day').valueOf(),
  pageNum: 0,
  pageSize: 9999,
}

const uncheckedQuery = {
  startAt: moment().startOf('day').valueOf(),
  endAt: moment().endOf('day').valueOf(),
  pageNum: 0,
  pageSize: 9999,
  checked: false,
}

type IProps = {
  lastUpdated: moment.Moment
} & Omit<IMobilePageDrawerProps, 'children'>

const AlarmDrawer = ({ lastUpdated, ...props }: IProps) => {
  const [activedTab, setActivedTab] = React.useState<'today' | 'pending'>(
    'today'
  )

  const {
    dataSource: alarmDataSource,
    refetch: refetchAlarm,
    isLoading,
  } = useAlarmList(query)

  const {
    dataSource: uncheckedAlarmDataSource,
    refetch: refetchUncheckedAlarm,
    isLoading: isUncheckedAlarmLoading,
  } = useAlarmList(uncheckedQuery)

  React.useEffect(() => {
    refetchAlarm()
    refetchUncheckedAlarm()
  }, [lastUpdated])

  const renderAlarmRows = () => {
    if (isLoading || isUncheckedAlarmLoading) {
      return (
        <div className="text-white pt-20">
          <Loading />
        </div>
      )
    }

    const dataSource =
      activedTab === 'today' ? alarmDataSource : uncheckedAlarmDataSource

    return (
      <div className="p-4 px-10 max-h-[calc(100dvh-90px)] overflow-auto">
        {dataSource?.map((alarm: Alarm) => (
          <Row key={alarm.id} className="text-base">
            <div className="">
              <div
                className={cx('font-medium text-2xl flex gap-5 items-center', {
                  'text-white': !alarm.isPopUp,
                  'text-danger': alarm.isPopUp,
                })}>
                <WarningFilled />
                <div>{alarm.displayName}</div>
              </div>
              <div>{alarm.alarmTypeDesc}</div>
            </div>
            <div>
              <div className="text-sm">
                {moment(alarm.createAt).format('YYYY/MM/DD HH:mm')}
              </div>

              <div className="flex items-center">
                {alarm.isPopUp ? '嚴重警報' : '一般警報'}
                <div className="mx-7">|</div>
                <div className="text-white">{alarm.sessionName}</div>
              </div>
            </div>
          </Row>
        ))}
      </div>
    )
  }

  return (
    <MobilePageDrawer title="警報事件" contentClassName="p-0" {...props}>
      <div className="flex">
        <Tab
          actived={activedTab === 'today'}
          onClick={() => setActivedTab('today')}>
          今日{` (${alarmDataSource.length || 0})`}
        </Tab>
        <Tab
          actived={activedTab === 'pending'}
          onClick={() => setActivedTab('pending')}>
          待處理
          {` (${uncheckedAlarmDataSource.length || 0})`}
        </Tab>
      </div>
      <div>{renderAlarmRows()}</div>
    </MobilePageDrawer>
  )
}

export default AlarmDrawer

const Tab = styled.div<{ actived?: boolean }>`
  font-size: 15px;
  color: #787878;
  padding: 0 8px;
  text-align: center;
  flex: 1;
  border-bottom: 1px solid #787878;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
  ${props =>
    props.actived &&
    `
    color: #41A4FF;
    border-bottom: 2px solid #41A4FF;
  `}
`

export const Row = styled.div`
  background-color: rgba(29, 29, 29, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.2);
  margin: 10px 0px;
  border-radius: 4px;
  padding: 10px;
  display: flex;
  justify-content: space-between;
  color: rgba(255, 255, 255, 0.5);
`
